import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Barcode, Delete, Hash, KeyRound, Lock, User as UserIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, http } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import type { BranchRow } from '../types';

/** Quick demo accounts matching the NoshOS design style */
const DEMO_ACCOUNTS = [
  { label: 'Admin', role: 'Superadmin', username: 'owner', pin: '1234', pw: 'Owner123!', color: 'text-amber-400' },
  { label: 'Cashier', role: 'Cashier', username: 'cashier', pin: '2345', pw: 'Cashier123!', color: 'text-sky-400' },
  { label: 'Waiter', role: 'Waiter', username: 'waiter', pin: '5678', pw: 'Waiter123!', color: 'text-purple-400' },
  { label: 'Kitchen', role: 'Chef', username: 'chef', pin: '3456', pw: 'Chef1234!', color: 'text-orange-400' },
  { label: 'Manager', role: 'Manager', username: 'manager', pin: '7890', pw: 'Manager123!', color: 'text-emerald-400' },
  { label: 'Inventory', role: 'Stock', username: 'inventory', pin: '4567', pw: 'Inventory12!', color: 'text-teal-400' },
];

export function LoginPage() {
  const signIn = useAuth((s) => s.signIn);
  const toast = useUi((s) => s.toast);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [authMode, setAuthMode] = useState<'pin' | 'barcode' | 'password'>('pin');
  const [identifier, setIdentifier] = useState('owner');
  const [pin, setPin] = useState('1234');
  const [password, setPassword] = useState('Owner123!');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: () => http.get<{ status: string; driver: string }>('/health'),
    retry: false,
  });
  const { data: branches } = useQuery({
    queryKey: ['branches-public'],
    queryFn: async () => {
      try { return (await http.get<{ branches: BranchRow[] }>('/branches')).branches; }
      catch { return null; }
    },
    retry: false,
  });

  useEffect(() => { setError(null); }, [identifier, pin, password]);

  const handleDigit = (digit: string) => {
    if (pin.length < 8) {
      const nextPin = pin + digit;
      setPin(nextPin);
      // If user is selecting a known demo PIN, keep the password in sync
      const matched = DEMO_ACCOUNTS.find((a) => a.pin === nextPin);
      if (matched) {
        setPassword(matched.pw);
        setIdentifier(matched.username);
      }
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  const submit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!identifier.trim()) {
      setError('Please enter a username or email.');
      return;
    }
    setBusy(true);
    setError(null);

    // If in PIN mode, find matching demo password or default to standard credential
    let credentialToUse = password;
    if (authMode === 'pin') {
      const demoMatch = DEMO_ACCOUNTS.find((a) => a.username === identifier.trim().toLowerCase() || a.pin === pin);
      if (demoMatch) {
        credentialToUse = demoMatch.pw;
      }
    }

    try {
      const user = await signIn(identifier.trim(), credentialToUse);
      toast({
        kind: 'success',
        title: `Welcome back, ${user.fullName.split(' ')[0]}`,
        message: `Signed in as ${user.roleName}.`,
      });
      navigate(from, { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setError(apiErr.message ?? 'Sign-in failed. Check your credentials.');
      if (apiErr.status === 423) {
        setError('Too many failed attempts. The account is temporarily locked.');
      }
    } finally {
      setBusy(false);
    }
  };

  const selectDemoAccount = (acc: typeof DEMO_ACCOUNTS[0]) => {
    setIdentifier(acc.username);
    setPin(acc.pin);
    setPassword(acc.pw);
    setError(null);
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-[#0d0d0e] px-4 py-8 text-neutral-100 select-none">
      {/* Background ambient glow matching the dark NoshOS aesthetic */}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-600/5 blur-[100px]" />

      <div className="relative z-10 flex w-full max-w-[420px] flex-col items-center">
        {/* Logo & App Header */}
        <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#161c28] via-[#10141f] to-[#0c0f17] p-2 shadow-2xl ring-1 ring-white/10">
          <div className="relative flex h-full w-full items-center justify-center rounded-xl bg-gradient-to-tr from-cyan-500 via-sky-400 to-indigo-500 shadow-inner">
            <span className="font-black text-2xl tracking-tighter text-white drop-shadow-md">N</span>
            <span className="absolute bottom-1 font-bold text-[7px] uppercase tracking-widest text-white/90">OS</span>
          </div>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-white drop-shadow-sm">NoshOS Demo POS</h1>
        <p className="mt-0.5 text-xs font-medium text-neutral-400 tracking-wide">Fine Dining Restaurant</p>

        {/* Main Terminal Card */}
        <div className="mt-6 w-full rounded-2xl border border-[#26262a] bg-[#141416]/95 p-5 shadow-2xl backdrop-blur-md">
          {/* Navigation / Mode Tabs */}
          <div className="flex border-b border-[#26262a] pb-3 text-xs font-medium text-neutral-400">
            <button
              type="button"
              onClick={() => setAuthMode('pin')}
              className={`relative flex flex-1 items-center justify-center gap-1.5 pb-2 transition-colors ${
                authMode === 'pin' ? 'font-bold text-amber-400' : 'hover:text-neutral-200'
              }`}
            >
              <Hash className="h-3.5 w-3.5" />
              <span>PIN</span>
              {authMode === 'pin' && (
                <span className="absolute -bottom-3 left-0 h-0.5 w-full bg-amber-400" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setAuthMode('barcode')}
              className={`relative flex flex-1 items-center justify-center gap-1.5 pb-2 transition-colors ${
                authMode === 'barcode' ? 'font-bold text-amber-400' : 'hover:text-neutral-200'
              }`}
            >
              <Barcode className="h-3.5 w-3.5" />
              <span>Barcode</span>
              {authMode === 'barcode' && (
                <span className="absolute -bottom-3 left-0 h-0.5 w-full bg-amber-400" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setAuthMode('password')}
              className={`relative flex flex-1 items-center justify-center gap-1.5 pb-2 transition-colors ${
                authMode === 'password' ? 'font-bold text-amber-400' : 'hover:text-neutral-200'
              }`}
            >
              <KeyRound className="h-3.5 w-3.5" />
              <span>Password</span>
              {authMode === 'password' && (
                <span className="absolute -bottom-3 left-0 h-0.5 w-full bg-amber-400" />
              )}
            </button>
          </div>

          {/* User Identifier Field */}
          <div className="mt-4">
            <div className="relative flex items-center rounded-xl border border-amber-500/40 bg-[#1a1a1d] px-3.5 py-2.5 transition-all focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-400">
              <UserIcon className="h-4 w-4 text-neutral-400" />
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Email or name"
                className="w-full bg-transparent px-2.5 text-sm text-neutral-100 placeholder-neutral-500 outline-none"
              />
            </div>
          </div>

          {/* Mode 1: PIN Pad */}
          {authMode === 'pin' && (
            <div className="mt-4 flex flex-col items-center">
              {/* PIN Dots Indicator */}
              <div className="mb-4 flex items-center justify-center gap-2">
                {[0, 1, 2, 3, 4, 5].map((idx) => {
                  const filled = pin.length > idx;
                  return (
                    <span
                      key={idx}
                      className={`h-2.5 w-2.5 rounded-full border transition-all duration-150 ${
                        filled
                          ? 'border-amber-400 bg-amber-400 scale-110 shadow-[0_0_8px_rgba(251,191,36,0.5)]'
                          : 'border-neutral-700 bg-neutral-800'
                      }`}
                    />
                  );
                })}
              </div>

              {/* Number Pad Grid */}
              <div className="grid w-full grid-cols-3 gap-2.5">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => handleDigit(num.toString())}
                    className="flex h-12 items-center justify-center rounded-xl bg-[#202024] font-semibold text-lg text-neutral-100 shadow-sm transition-all hover:bg-[#2b2b30] active:scale-95 active:bg-[#34343a]"
                  >
                    {num}
                  </button>
                ))}
                <div />
                <button
                  type="button"
                  onClick={() => handleDigit('0')}
                  className="flex h-12 items-center justify-center rounded-xl bg-[#202024] font-semibold text-lg text-neutral-100 shadow-sm transition-all hover:bg-[#2b2b30] active:scale-95 active:bg-[#34343a]"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={handleBackspace}
                  className="flex h-12 items-center justify-center rounded-xl bg-[#361a1a]/80 text-rose-400 shadow-sm transition-all hover:bg-rose-950 hover:text-rose-300 active:scale-95"
                  aria-label="Backspace"
                >
                  <Delete className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Mode 2: Barcode Scanner Simulation */}
          {authMode === 'barcode' && (
            <div className="mt-5 flex flex-col items-center justify-center rounded-xl border border-dashed border-neutral-700 bg-[#1a1a1d] p-6 text-center">
              <Barcode className="h-12 w-12 text-amber-400/80 animate-pulse" />
              <p className="mt-3 text-sm font-medium text-neutral-200">Scan Staff Badge or Card</p>
              <p className="mt-1 text-xs text-neutral-500">
                Ready for barcode scanner hardware input
              </p>
            </div>
          )}

          {/* Mode 3: Traditional Password */}
          {authMode === 'password' && (
            <div className="mt-4">
              <div className="relative flex items-center rounded-xl border border-[#2e2e33] bg-[#1a1a1d] px-3.5 py-2.5 focus-within:border-amber-400">
                <Lock className="h-4 w-4 text-neutral-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter account password"
                  className="w-full bg-transparent px-2.5 text-sm text-neutral-100 placeholder-neutral-500 outline-none"
                />
              </div>
            </div>
          )}

          {/* Error Message banner */}
          {error && (
            <div className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/20 px-3 py-2 text-center text-xs text-rose-300">
              {error}
            </div>
          )}

          {/* Sign In Action Button */}
          <button
            type="button"
            onClick={() => submit()}
            disabled={busy}
            className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 font-semibold text-sm text-neutral-950 shadow-lg shadow-amber-500/20 transition-all hover:from-amber-400 hover:to-amber-500 active:scale-[0.98] disabled:opacity-50"
          >
            {busy ? (
              <span className="animate-spin text-sm">⏳</span>
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                <span>Sign In</span>
              </>
            )}
          </button>
        </div>

        {/* Demo Accounts Panel */}
        <div className="mt-5 w-full rounded-2xl border border-[#26262a] bg-[#141416]/95 p-4 shadow-xl">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
            DEMO ACCOUNTS — CLICK TO FILL
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {DEMO_ACCOUNTS.map((acc) => (
              <button
                key={acc.label}
                type="button"
                onClick={() => selectDemoAccount(acc)}
                className="flex flex-col items-start rounded-xl border border-[#26262a] bg-[#1a1a1d] p-2.5 text-left transition-all hover:border-amber-500/40 hover:bg-[#202024] active:scale-95"
              >
                <span className={`text-xs font-bold ${acc.color}`}>{acc.label}</span>
                <span className="mt-0.5 text-[11px] font-mono text-neutral-400">PIN: {acc.pin}</span>
              </button>
            ))}
          </div>

          <div className="mt-3.5 flex items-center justify-between text-[11px] text-neutral-500 border-t border-[#222226] pt-2.5">
            <span>
              {branches?.length ? `${branches.length} branches connected` : 'Ready to authenticate'}
            </span>
            <span>
              {health?.driver ? `DB: ${health.driver}` : 'Connected'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
